
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Plus, Calendar, User, FileText } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';

interface TimelineEvent {
  id: string;
  date: string;
  title: string;
  description: string;
  type: 'document' | 'hearing' | 'deadline' | 'note' | 'other';
  user: string;
}

interface CaseTimelineProps {
  caseId: string;
  events?: TimelineEvent[];
}

export const CaseTimeline = ({ caseId, events = [] }: CaseTimelineProps) => {
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    type: 'note' as TimelineEvent['type'],
    date: new Date().toISOString().split('T')[0]
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  const handleAddEvent = async () => {
    // Aqui integraria com o backend
    toast({
      title: "Evento adicionado",
      description: "O evento foi adicionado à timeline do processo"
    });
    setIsDialogOpen(false);
    setNewEvent({
      title: '',
      description: '',
      type: 'note',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const getEventIcon = (type: TimelineEvent['type']) => {
    switch (type) {
      case 'document': return <FileText className="w-4 h-4" />;
      case 'hearing': return <Calendar className="w-4 h-4" />;
      case 'deadline': return <Calendar className="w-4 h-4" />;
      case 'note': return <User className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getEventColor = (type: TimelineEvent['type']) => {
    switch (type) {
      case 'document': return 'bg-blue-500';
      case 'hearing': return 'bg-purple-500';
      case 'deadline': return 'bg-red-500';
      case 'note': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Timeline do Processo
          </CardTitle>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Evento
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Novo Evento na Timeline</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="event-title">Título</Label>
                  <Input
                    id="event-title"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                    placeholder="Ex: Audiência de conciliação"
                  />
                </div>
                <div>
                  <Label htmlFor="event-description">Descrição</Label>
                  <Textarea
                    id="event-description"
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                    placeholder="Detalhes do evento..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="event-date">Data</Label>
                  <Input
                    id="event-date"
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleAddEvent}>
                    Adicionar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {events.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum evento registrado ainda.</p>
              <p className="text-sm">Adicione eventos para acompanhar o progresso do processo.</p>
            </div>
          ) : (
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border"></div>
              {events.map((event, index) => (
                <div key={event.id} className="relative flex items-start gap-4 pb-6">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${getEventColor(event.type)} text-white relative z-10`}>
                    {getEventIcon(event.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold">{event.title}</h4>
                      <Badge variant="outline" className="text-xs">
                        {event.type}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {event.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{new Date(event.date).toLocaleDateString('pt-BR')}</span>
                      <span>por {event.user}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
